import axiosNest from '../../api/axiosNest';
import { IMPERSONATE_PATH } from '../../session/sessionToken';

/**
 * „Zaloguj jako” (decyzja Bartka 02.11.2026, przełącznik `impersonation`,
 * S-02.11-01) — po stronie ekranu: kiedy pokazać przycisk i jak otworzyć
 * nową kartę. Rozstrzyga API (`auth/impersonation.ts`): reguła tutaj tylko
 * chowa przycisk tam, gdzie serwer i tak odmówi.
 */

/** Klucz przełącznika (dane — nie przemianowujemy). */
export const FEATURE_IMPERSONATION = 'impersonation';

/** Powód — jak w API (`IMPERSONATION_REASON_MIN/MAX`). */
export const IMPERSONATION_REASON_MIN = 10;
export const IMPERSONATION_REASON_MAX = 500;

/** Role, których nie przejmuje nikt — administracyjne i maszynowe. */
export const PROTECTED_TARGET_ROLES: readonly string[] = ['ADMIN', 'COMPANY_ADMIN', 'API_CLIENT'];

/** Kto przejmuje — z tokenu zalogowanej karty. */
export interface ImpersonationActor {
  id: number | undefined;
  role: string | undefined;
  companyId: number | null | undefined;
}

/**
 * Czy pokazać „Zaloguj jako” przy tym koncie: nie siebie, nie konta
 * administracyjne i maszynowe, a administrator spółki — tylko konta swojej
 * spółki. Konto z rolą własną o uprawnieniach administracyjnych rozpoznaje
 * dopiero API (odmowa z komunikatem).
 */
export function canImpersonateTarget(
  actor: ImpersonationActor,
  target: { id?: unknown; role?: unknown; companyId?: unknown } | null | undefined,
): boolean {
  if (!target || actor.id === undefined) return false;
  if (actor.role !== 'ADMIN' && actor.role !== 'COMPANY_ADMIN') return false;
  if (Number(target.id) === actor.id) return false;
  const role = typeof target.role === 'string' ? target.role : '';
  if (role === '' || PROTECTED_TARGET_ROLES.includes(role)) return false;
  if (actor.role === 'COMPANY_ADMIN') {
    if (typeof actor.companyId !== 'number') return false;
    if (target.companyId !== actor.companyId) return false;
  }
  return true;
}

/** Powód po przycięciu albo komunikat. */
export function reasonProblem(reason: string): string | null {
  const t = reason.trim();
  if (t.length < IMPERSONATION_REASON_MIN) {
    return `Podaj powód — co najmniej ${IMPERSONATION_REASON_MIN} znaków.`;
  }
  if (t.length > IMPERSONATION_REASON_MAX) {
    return `Powód może mieć najwyżej ${IMPERSONATION_REASON_MAX} znaków.`;
  }
  return null;
}

/**
 * Adres nowej karty — trasa i kod we fragmencie adresu (`/#/impersonate?code=…`):
 * fragment nie idzie do serwera, do jego dziennika ani w nagłówku `Referer`.
 */
export function impersonationUrl(origin: string, code: string): string {
  return `${origin}/#${IMPERSONATE_PATH}?code=${encodeURIComponent(code)}`;
}

/** Kod z fragmentu adresu (`#/impersonate?code=…`) albo `null`. */
export function codeFromHash(hash: string): string | null {
  const m = /[?&#]code=([^&]+)/.exec(hash);
  if (!m) return null;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return null;
  }
}

/** Start przejęcia: kod jednorazowy (60 s) do nowej karty. */
export async function startImpersonation(userId: number, reason: string): Promise<string> {
  const res = await axiosNest.post<{ code: string }>('/auth/impersonate', {
    userId,
    reason: reason.trim(),
  });
  return res.data.code;
}

/** Komunikat z odpowiedzi API albo ogólny. */
export function apiMessage(e: unknown, fallback: string): string {
  const data = (e as { response?: { data?: { message?: unknown } } })?.response?.data;
  const m = data?.message;
  if (typeof m === 'string' && m.trim() !== '') return m;
  if (Array.isArray(m) && typeof m[0] === 'string') return m[0];
  return fallback;
}

// ── Teksty dla operatora ──────────────────────────────────────────────

export const BUTTON_LABEL = 'Zaloguj jako';
export const DIALOG_NOTE =
  'Otworzy się nowa karta, w której pracujesz jako ten użytkownik — z jego rolą, uprawnieniami i spółką. Twoja karta zostaje na Twoim koncie. Każda zmiana trafi do dziennika działań z Twoim nazwiskiem, a sesja trwa najwyżej godzinę. W tej karcie nie zmienisz kont, ról, poświadczeń ani przełączników funkcji.';
export const START_ERROR = 'Nie udało się zalogować jako ten użytkownik.';
export const POPUP_BLOCKED =
  'Przeglądarka zablokowała nową kartę. Zezwól tej stronie na otwieranie okien i spróbuj jeszcze raz.';
